import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return bytes + ' B';
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const StatCard = ({ icon, label, value, sub, color, pending, pendingLink }) => (
  <div className={`bg-white rounded-2xl border shadow-sm p-5 flex items-center gap-4 ${pending > 0 ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'}`}>
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${color}`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
      <p className="text-sm font-medium text-slate-600 mt-1">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      {pending > 0 && (
        pendingLink
          ? <a href={pendingLink} className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full animate-pulse">
              <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
              {pending} pending approval
            </a>
          : <span className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full animate-pulse">
              <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
              {pending} pending approval
            </span>
      )}
    </div>
  </div>
);

const UserDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [diskQuotaBytes, setDiskQuotaBytes] = useState(null);
  const [diskConsumedBytes, setDiskConsumedBytes] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [stats, setStats] = useState({ events: 0, batches: 0, photos: 0, verified: 0, downloads: 0 });

  useEffect(() => {
    Promise.all([fetchMe(), fetchStats()]).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMe = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`);
      setDiskQuotaBytes(res.data.disk_quota_bytes ?? null);
      setDiskConsumedBytes(res.data.disk_consumed_bytes ?? 0);
      setSubscription(res.data.subscription ?? null);
    } catch (_) {}
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/events`);
      const events = res.data || [];
      let batches = 0, pendingBatches = 0, photos = 0, verified = 0, downloads = 0;
      events.forEach((ev) => {
        batches += ev.batch_count || 0;
        pendingBatches += ev.pending_batch_count || 0;
        photos += ev.photo_count || 0;
        verified += ev.face_verified_count ?? ev.verification_count ?? 0;
        downloads += (ev.download_image_count ?? 0) + (ev.download_zip_count ?? 0);
      });
      setStats({ events: events.length, batches, pendingBatches, photos, verified, downloads });
    } catch (_) {}
  };

  const remainingBytes = diskQuotaBytes != null && diskQuotaBytes > 0
    ? Math.max(0, diskQuotaBytes - diskConsumedBytes)
    : null;
  const usagePercent = diskQuotaBytes != null && diskQuotaBytes > 0
    ? Math.round((diskConsumedBytes / diskQuotaBytes) * 100)
    : null;

  const isOnTrial = subscription?.subscription?.status === 'trial';
  const subStatus = subscription?.subscription?.status;
  const planName = subscription?.plan?.name;
  const subExpiresAt = subscription?.subscription?.expires_at;
  const expiryGrace = subscription?.expiry_grace;
  const expiresInDays = expiryGrace?.expires_in_days ?? null;
  const inGracePeriod = expiryGrace?.in_grace_period === true;
  const daysUntilDeletion = expiryGrace?.days_until_deletion ?? null;
  const gracePeriodEndsAt = expiryGrace?.grace_period_ends_at;
  const expiringSoon = expiresInDays != null && expiresInDays >= 1 && expiresInDays <= 7;
  const subAllowed = subscription?.allowed === true;
  const isPending = subStatus === 'pending_approval';
  const subscriptionExpired = subscription != null && !subAllowed && !isPending;

  const planLabel = planName || (isOnTrial ? 'Trial' : null);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysLeft = subExpiresAt
    ? Math.ceil((new Date(subExpiresAt).getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
    : null;

  const displayName = user?.name || user?.phone || 'there';

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">

      {/* Subscription / trial alerts */}
      {subscriptionExpired && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-rose-50 border border-rose-200">
          <div className="w-8 h-8 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.834-1.964-.834-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-rose-900">Subscription expired</p>
            <p className="text-xs text-rose-700 mt-0.5">Upload, create event, and verification are disabled until you subscribe again.</p>
          </div>
          <Link to="/subscription" className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 transition-colors">Subscribe</Link>
        </div>
      )}
      {inGracePeriod && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-rose-50 border border-rose-200">
          <div className="w-8 h-8 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-rose-900">Plan expired — grace period</p>
            <p className="text-xs text-rose-700 mt-0.5">Renew within {daysUntilDeletion} day{daysUntilDeletion !== 1 ? 's' : ''} or all your data will be permanently deleted.{gracePeriodEndsAt && ` Deletion date: ${new Date(gracePeriodEndsAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}.`}</p>
          </div>
          <Link to="/subscription" className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 transition-colors">Renew</Link>
        </div>
      )}
      {expiringSoon && !inGracePeriod && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
          <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.834-1.964-.834-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">Plan expiring in {expiresInDays} day{expiresInDays !== 1 ? 's' : ''}</p>
            <p className="text-xs text-amber-700 mt-0.5">Renew to avoid a 7-day grace period after which all data is permanently deleted.</p>
          </div>
          <Link to="/subscription" className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 transition-colors">Renew</Link>
        </div>
      )}
      {isOnTrial && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-violet-50 border border-violet-200">
          <div className="w-8 h-8 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-violet-900">Trial plan active</p>
            <p className="text-xs text-violet-700 mt-0.5">{subExpiresAt ? `Expires ${new Date(subExpiresAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}.` : 'Your trial is active.'} Upgrade for more storage and features.</p>
          </div>
          <Link to="/subscription" className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors">Upgrade</Link>
        </div>
      )}

      {/* Welcome */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-6 flex items-center justify-between gap-4 flex-wrap" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
          <div>
            <p className="text-violet-300 text-sm font-medium">{getGreeting()}</p>
            <h1 className="text-2xl font-bold text-white mt-0.5">{displayName} 👋</h1>
            <p className="text-violet-200 text-sm mt-1">Here&apos;s a summary of your Pixel App activity.</p>
          </div>
          <Link
            to="/events"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-violet-900 text-sm bg-white hover:bg-violet-50 transition-colors shadow-sm flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            New Event
          </Link>
        </div>

        {/* Plan status strip */}
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap"
          style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', borderTopColor: 'rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Plan pill */}
            {subAllowed && planLabel && (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                isOnTrial
                  ? 'bg-violet-400/30 text-violet-100 border border-violet-400/40'
                  : 'bg-emerald-400/20 text-emerald-200 border border-emerald-400/30'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOnTrial ? 'bg-violet-300' : 'bg-emerald-400'}`} />
                {isOnTrial ? 'Trial' : planLabel}
              </span>
            )}
            {isPending && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-400/20 text-amber-200 border border-amber-400/30">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300 flex-shrink-0" />
                Pending approval
              </span>
            )}
            {subscriptionExpired && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-400/20 text-rose-200 border border-rose-400/30">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                No active plan
              </span>
            )}
            {/* Days remaining */}
            {subAllowed && daysLeft != null && (
              <span className={`text-xs font-medium ${
                daysLeft <= 7 ? 'text-amber-300' : daysLeft <= 30 ? 'text-violet-300' : 'text-violet-400'
              }`}>
                {daysLeft > 0 ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining` : 'Expires today'}
              </span>
            )}
          </div>
          <Link to="/subscription" className="text-xs text-violet-300 hover:text-white font-medium transition-colors flex-shrink-0">
            View plan →
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Events"
          value={stats.events}
          color="bg-violet-100"
          icon={<svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
        />
        <StatCard
          label="Batches"
          value={stats.batches}
          color={stats.pendingBatches > 0 ? "bg-amber-100" : "bg-blue-100"}
          pending={stats.pendingBatches}
          pendingLink="/batches"
          icon={<svg className={`w-6 h-6 ${stats.pendingBatches > 0 ? 'text-amber-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>}
        />
        <StatCard
          label="Photos"
          value={stats.photos}
          color="bg-emerald-100"
          icon={<svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
        />
        <StatCard
          label="Face verified"
          value={stats.verified}
          sub={stats.downloads > 0 ? `${stats.downloads} downloads` : undefined}
          color="bg-amber-100"
          icon={<svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
        />
      </div>

      {/* Storage */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Storage</h2>
            {usagePercent != null && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${usagePercent >= 90 ? 'bg-rose-100 text-rose-700' : usagePercent >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {usagePercent}% used
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Allowed</p>
              <p className="text-xl font-bold text-slate-800">
                {diskQuotaBytes != null && diskQuotaBytes > 0 ? formatBytes(diskQuotaBytes) : '∞'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Used</p>
              <p className="text-xl font-bold text-slate-800">{formatBytes(diskConsumedBytes)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Free</p>
              <p className="text-xl font-bold text-slate-800">
                {remainingBytes != null ? formatBytes(remainingBytes) : '∞'}
              </p>
            </div>
          </div>
          {usagePercent != null && (
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${usagePercent >= 90 ? 'bg-rose-500' : usagePercent >= 70 ? 'bg-amber-400' : 'bg-violet-500'}`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
          )}
        </div>
        <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-400">Deleting batches or events frees space.</p>
          <Link to="/subscription" className="text-xs text-violet-600 font-semibold hover:underline">View plan</Link>
        </div>
      </div>

    </div>
  );
};

export default UserDashboard;
